const routes = {
  basePath: '',
  home: '/',
  orders: 'orders',
  gigOrderCreate: 'creategigorder',
  // Legacy route key — `/orders/create-order-v2`. Kept as a redirect to
  // `/orders-v2/create` so existing links/bookmarks don't break.
  gigOrderCreateV2: 'create-order-v2',
  orderDetail: ':orderId',

  // Orders V2 namespace. Today only `create` is wired; future list/detail/
  // templates pages will mount as additional children under `ordersV2`.
  ordersV2: 'orders-v2',
  ordersV2Create: 'create',

  // Dashboard V2 (risk-surfacing ops dashboard). New standalone route; the
  // legacy dashboard stays mounted at basePath ("/") and is untouched.
  dashboardV2: 'dashboard-v2',
  orderJobDetail: ':orderId/jobs/:jobId',
  orderJobDetailOld: ':orderId/jobsold/:jobId',

  clients: 'clients',
  customerDetail: ':customerId',
  customerAccountDetail: ':customerId/accounts/:accountId',
  customerAdminDetail: ':customerId/admin/:adminId',

  contacts: 'contacts',

  invoices: 'invoices',
  invoicesDetail: ':invoiceId',
  invoicesPay: ':invoiceId/pay',
  invoiceHealth: 'invoice-health',
  invoiceGenerator: 'invoice-generator',
  invoiceGeneratorDetail: 'invoice-generator?clientId',
  restrictedAccess: 'restricted-access',
  workers: 'workers',
  workerDetail: ':workerId',

  // Workers V2
  workersV2: 'workers_v2',
  workerV2Detail: ':workerId',
  workerV2SkillDetail: ':workerId/skill/:skillId',

  chat: 'chat',
  // Supabase worker chat (formerly /chat-v2). The legacy paths below
  // hard-redirect here — see routes/index.tsx.
  inbox: 'inbox',
  // Legacy chat paths — redirect-only.
  inappchat: 'inappchat',
  chatV2: 'chat-v2',
  // Chat V2 cohort management — see/add/remove workers on the Supabase
  // comms allowlist. Ops-only (see routes/index.tsx).
  chatV2Cohort: 'chat-v2-cohort',

  // Customer Admin
  myTeam: 'my-team',
  myTeamAdminDetail: ':adminId',
  myCompany: 'my-company',
  myCompanyAccountDetail: 'accounts/:accountId',
  myProfile: 'my-profile',

  // Payroll Runs
  payrolls: 'payrolls',
  payrollDetail: ':payrollId',
  // Payroll Runs V2
  payrollsV2: 'payrolls-v2',
  payrollDetailV2: ':payrollId',

  // Reports
  reports: 'reports',

  jobmonitor: 'job-monitor',
  notificationHub: 'notification-hub',

  login: 'login',
  signUp: 'sign-up',
  signOut: 'sign-out',
  forgotPassword: 'forgot-password',
  resetPassword: 'reset-password',
  validationAccount: 'validation-account/:caseId',
  linkExpire: 'link-expire',
  payInvoice: 'pay-invoice/:invoiceId',

  mobileIntro: 'mobile-intro',
  appLogin: 'app-login',
  requestAppAccess: 'request-app-access',
  waitlist: 'waitlist',

  // PDF Generation
  pdf_generation: 'pdf_generation',

  // QR Download
  qrDownload: 'qr/:pdf_auth_key',

  // Data Deletion Request (public)
  dataDeletionRequest: 'data-deletion-request',

  // Payroll Audit
  payrollAudit: 'payroll-audit',

  // Background Audit
  backgroundAudit: 'background-audit',

  // Compliance
  compliance: 'compliance',

  // WorkBright Audit
  workbrightAudit: 'workbright_audit',

  // Zeal Audit
  zealAudit: 'zeal_audit',

  // HelloSign Document Audit
  hellosignAudit: 'hellosign-audit',

  // Timesheet Health Checks
  timesheetHealthChecks: 'timesheet_healthchecks',

  // Worker Exclusion Analyzer
  workerExclusionAnalyzer: 'worker-exclusion-analyzer',

  // Onboarding Bypass
  onboardingBypass: 'onboarding-bypass',
  // I-9 Bypass
  i9Bypass: 'i9-bypass',
  // Dev Password Reset
  passwordReset: 'password-reset-dev',
  // Order Invites
  orderInvites: 'order-invites',

  // Job Debugger
  jobDebugger: 'jobs/:jobId/debug',

  // Job Creator
  jobCreator: 'job-creator',

  // Document Signing (public - worker WebView)
  documentSigning: 'document-signing',

  // Posts Management
  posts: 'posts',
  postCreate: 'create',
  postEdit: ':postId/edit',

  // Document Management
  documents: 'documents',
  documentCreate: 'create',
  documentShow: ':documentId',
  documentEdit: ':documentId/edit',
  documentTestSign: ':documentId/test-sign',

  // AI Assessments
  aiAssessments: 'ai_assessments',
  assessmentHealth: 'assessment_health',
  // Vexo Analytics — operator console (TENANT_ADMIN only)
  analytics: 'analytics',
  // In-house Analytics Data dashboard — read-only ops surface over the
  // AnalyticsEvent pipeline (TENANT_ADMIN only). Separate page from the Vexo
  // console above; first React.lazy route in the app (recharts stays out of
  // the main bundle).
  analyticsDashboard: 'analytics-dashboard',
  // Skill Management
  skillManagement: 'skill-management',
  pendingUniforms: 'uniforms',
  pendingCertificates: 'certificates',

  // Worker Accountability (Legacy)
  accountability: 'accountability',
  accountabilityViolations: 'violations',
  accountabilitySuspensions: 'suspensions',
  accountabilityAppeals: 'appeals',
  accountabilityStats: 'stats',

  // No-Show Alerts
  noshowAlerts: 'noshow-alerts',

  // SMS Debug Logs
  smsLogs: 'sms-logs',

  // Internal QA tool — direct-send an announcement to specific test workers.
  testAnnouncement: 'test-announcement',
  // Same tool, sending via Supabase Chat V2 broadcasts.
  testAnnouncementV2: 'test-announcement-v2',

  // Skills Admin
  skills: 'skills',

  // Regions Management
  regions: 'regions',

  // Version Controls (consolidated OTA gate + binary force-update gate)
  versionControls: 'version-controls',

  // Client Sessions (hidden fleet view — accessible only via direct URL)
  clientSessions: 'client-sessions',

  // Actions Log (hidden internal-only shift_events feed — direct URL only)
  actionsLog: 'shift-event-logs',

  // Feature Flags & Experiments (graphy's native A/B engine — ops UI)
  featureFlags: 'feature-flags',

  // Tasks (New Accountability UI)
  tasks: 'tasks',
  tasksViolations: 'violations',
  tasksViolationDetail: 'violations/:violationId',
  tasksAppeals: 'appeals',
  tasksSuspensions: 'suspensions',
  tasksStats: 'stats',
  tasksViolationApprovers: 'violation-approvers',
};

export default routes;
